<template>
  <modal-window :main-width="12">
    <div v-if="loading" class="text-center">
      <loading-spinner></loading-spinner>
    </div>
    <div v-if="!loading">
      <h2>Active Schedules</h2>
      <div class="schedules-list">
        <table class="table table-striped">
          <thead>
            <tr>
              <th style="width: 64%">Name</th>
              <th style="width: 10%; text-align: center">Timezone</th>
              <th style="width: 10%; text-align: center">Planned</th>
              <th style="width: 10%; text-align: center">Due</th>
              <th style="width: 18%"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="templateSchedules.length === 0">
              <td colspan="5">
                <i class="far fa-info-circle"></i>
                &nbsp; No Schedules defined
              </td>
            </tr>

            <tr v-for="schedule in templateSchedules" :key="schedule.id">
              <td>{{ schedule.description }}</td>
              <td style="text-align: center" class="text-nowrap">
                <div v-if="schedule.scheduleType === 'Date' && !schedule.cronExpressionIsLocalTime" class="badge badge-primary">UTC</div>
                <div v-if="schedule.scheduleType === 'Date' && schedule.cronExpressionIsLocalTime" class="badge badge-secondary">LOCAL</div>
              </td>
              <td class="text-center">
                <div class="badge badge-primary" v-if="schedule.plannedMinutesAfterCreation">
                  <i class="far fa-clock"></i>
                  {{ schedule.plannedMinutesAfterCreation }} mins
                </div>
                <div v-else class="badge badge-secondary">N/A</div>
              </td>
              <td class="text-center">
                <div class="badge badge-primary" v-if="schedule.dueMinutesAfterCreation">
                  <i class="far fa-clock"></i>
                  {{ schedule.dueMinutesAfterCreation }} mins
                </div>
                <div v-else class="badge badge-secondary">N/A</div>
              </td>
              <td>
                <div class="badgebutton danger active text-center text-nowrap" @click="confirmDeleteSchedule(schedule.id)">
                  <i class="far fa-trash mr-1"></i>
                  Delete
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <h2>Add Schedule</h2>
      <hr />

      <div class="label mb-2">Schedule Type</div>
      <b-form-group>
        <b-form-radio-group v-model="newSchedule.scheduleType" name="radio">
          <b-form-radio name="option" :value="boardItemScheduleType.Date">Date</b-form-radio>
          <b-form-radio name="option" :value="boardItemScheduleType.ShiftStart">At Shift handover</b-form-radio>
        </b-form-radio-group>
      </b-form-group>

      <template v-if="isScheduleTypeDateSelected">
        <label class="label mb-2">Specify time interval</label>
        <section v-on:click="isMonthlySelected">
          <vue-cron-editor :visibleTabs="cronTabs" :cronSyntax="'quartz'" v-model="newSchedule.cronExpression" id="schedule-cron-editor">
            <template>fddfdfdf</template>
          </vue-cron-editor>
        </section>

        <div v-if="isMonthlySelectedState" class="pl-3 pr-3 col-4 mb-3">
          <div class="label mb-2">Starting in</div>
          <select class="form-control form-control-sm" v-model="monthlyStartingAt">
            <option :value="null">Select start month</option>
            <option v-for="option in monthlyStartingAtOptions" :value="option.id" :key="option.id">{{ option.name }}</option>
          </select>
        </div>

        <b-form-checkbox class="ml-4 mb-1" v-model="newSchedule.cronExpressionIsLocalTime" :unchecked-value="false">
          Local Time
        </b-form-checkbox>
      </template>

      <template v-else>
        <div class="label mb-2">Specify Shift</div>
        <select class="form-control form-control-sm" v-model="newSchedule.shiftTypeId">
          <option :value="null">All shifts</option>
          <option v-for="option in shiftTypeOptions" :value="option.id" :key="option.id">{{ option.name }}</option>
        </select>
      </template>

      <hr />
      <label class="label mb-2">
        Advanced Settings
        <i class="far fa-info-circle" v-b-tooltip title="0 means no planned/ due date will be set for the newly created item"></i>
      </label>
      <div class="row">
        <div class="col-6">
          <div class="label">Planned</div>
          <div class="input-group input-group-sm mb-3">
            <input type="number" class="form-control form-control-sm" placeholder="" v-model="newSchedule.plannedMinutesAfterCreation" />
            <div class="input-group-append">
              <div class="input-group-text input-gr">minutes after creation</div>
            </div>
          </div>
        </div>

        <div class="col-6">
          <div class="label">Due</div>
          <div class="input-group input-group-sm mb-3">
            <input type="number" class="form-control form-control-sm" placeholder="" v-model="newSchedule.dueMinutesAfterCreation" />
            <div class="input-group-append">
              <div class="input-group-text input-gr">minutes after creation</div>
            </div>
          </div>
        </div>
      </div>

      <hr />
      <div class="action-bar d-flex justify-content-end">
        <b-alert show variant="info" v-if="warningMessage" class="mr-2" @click.native="clearWarning()">
          <i class="far fa-exclamation-triangle"></i>
          &nbsp;
          {{ warningMessage }}
        </b-alert>
        <button class="badgebutton success active text-center" @click="addSchedule()" :disabled="!isScheduleDataValid">
          <i class="far fa-plus"></i>
          Add Schedule
        </button>
      </div>
    </div>
  </modal-window>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import { boardItemTemplateScheduleClient } from '@/apiclient/opsboard-api';
  import {
    BoardItemScheduleType,
    BoardItemTemplateScheduleDto,
    BoardItemTemplateScheduleInsertDto,
    ShiftTypeDto,
  } from '@/apiclient/apiclient_generated';

  import VueCronEditorBuefy from 'vue-cron-editor-buefy';
  import LoadingSpinner from '@/components/Misc/LoadingSpinner.vue';
  import { Prop } from 'vue-property-decorator';
  import ModalWindow from '@/components/ModalWindow.vue';
  import { GlobalHelper } from '@/helpers/global-helper';
  import { ConfirmModal } from '@/helpers/confirm-modal';
  import { BoardStore } from '@/store/board';
  import { orderBy } from 'lodash';

  @Component({ components: { ModalWindow, LoadingSpinner, 'vue-cron-editor': VueCronEditorBuefy } })
  export default class BoardItemScheduleEdit extends Vue {
    @Prop() currentTemplateId: string;

    private templateSchedules: BoardItemTemplateScheduleDto[] = [];
    private newSchedule: BoardItemTemplateScheduleInsertDto = BoardItemTemplateScheduleInsertDto.fromJS({
      scheduleType: BoardItemScheduleType.ShiftStart,
      cronExpression: '',
      boardItemId: '',
      plannedMinutesAfterCreation: 0,
      dueMinutesAfterCreation: 0,
    });

    cronTabs = ['daily', 'weekly', 'monthly', 'advanced'];
    monthlyStartingAt = null;
    monthlyStartingAtOptions = [
      { id: 1, name: 'January' },
      { id: 2, name: 'February' },
      { id: 3, name: 'March' },
      { id: 4, name: 'April' },
      { id: 5, name: 'May' },
      { id: 6, name: 'June' },
      { id: 7, name: 'July' },
      { id: 8, name: 'August' },
      { id: 9, name: 'September' },
      { id: 10, name: 'October' },
      { id: 11, name: 'November' },
      { id: 12, name: 'December' },
    ];

    private warningMessage = '';
    boardItemScheduleType = BoardItemScheduleType;

    private loading = true;
    private boardId: string;
    isMonthlySelectedState = false;

    created() {
      this.boardId = this.$route.params.id;
    }

    get shiftTypeOptions(): ShiftTypeDto[] {
      return orderBy(BoardStore.board.shiftTypes, 'sortOrder');
    }

    async mounted() {
      this.initNewSchedule();
      await BoardStore.getBoard(this.boardId);
      await this.loadData();
    }

    async loadData() {
      this.loading = true;
      this.templateSchedules = await boardItemTemplateScheduleClient.get(this.currentTemplateId);
      this.loading = false;
    }

    initNewSchedule() {
      this.newSchedule.scheduleType = BoardItemScheduleType.Date;
      this.newSchedule.cronExpression = '0 0 0 ? * MON';
      this.newSchedule.boardItemId = this.currentTemplateId;
      this.newSchedule.cronExpressionIsLocalTime = false;
      this.newSchedule.shiftTypeId = null;
      this.newSchedule.plannedMinutesAfterCreation = 0;
      this.newSchedule.dueMinutesAfterCreation = 0;

      this.resetMonthlyState();
    }

    get isScheduleTypeDateSelected() {
      return this.newSchedule.scheduleType == BoardItemScheduleType.Date;
    }

    get isScheduleDataValid() {
      return (
        this.newSchedule.scheduleType == BoardItemScheduleType.ShiftStart ||
        (this.newSchedule.scheduleType == BoardItemScheduleType.Date && this.newSchedule.cronExpression)
      );
    }

    confirmDeleteSchedule(id: string) {
      const success = () => {
        this.deleteSchedule(id);
      };

      ConfirmModal.confirm(
        this,
        `Are you sure you want to delete this schedule?`,
        'Confirm delete',
        'Yes, delete',
        'No',
        success,
        null,
        'danger'
      );
    }

    async deleteSchedule(id: string) {
      try {
        await boardItemTemplateScheduleClient.delete(id);
        //lade schedules neu
        this.templateSchedules = await boardItemTemplateScheduleClient.get(this.currentTemplateId);
        GlobalHelper.NotifySuccess('Schedule deleted');
      } catch (error) {
        GlobalHelper.NotifyError('Schedule could not be deleted!');
      }
    }

    async addSchedule() {
      try {
        if (this.newSchedule.scheduleType == BoardItemScheduleType.ShiftStart) {
          this.newSchedule.cronExpression = '';
        }

        if (this.newSchedule.scheduleType == BoardItemScheduleType.Date) {
          this.newSchedule.shiftTypeId = null;
        }

        const existingSchedules = await boardItemTemplateScheduleClient.get(this.currentTemplateId);
        const sameSchedules = existingSchedules.find(
          (existing) =>
            (existing.scheduleType == BoardItemScheduleType.ShiftStart &&
              this.newSchedule.scheduleType == BoardItemScheduleType.ShiftStart &&
              existing.shiftTypeId == this.newSchedule.shiftTypeId) ||
            (existing.scheduleType == BoardItemScheduleType.Date &&
              this.newSchedule.scheduleType == BoardItemScheduleType.Date &&
              existing.cronExpression == this.newSchedule.cronExpression &&
              existing.cronExpressionIsLocalTime == this.newSchedule.cronExpressionIsLocalTime)
        );

        if (sameSchedules) {
          this.warningMessage = 'Schedule with same settings already exists!';
        } else {
          this.newSchedule.cronExpression = this.rebuildCronExpression(this.newSchedule.cronExpression);

          await boardItemTemplateScheduleClient.post(this.newSchedule);
          //lade schedules neu
          this.templateSchedules = await boardItemTemplateScheduleClient.get(this.currentTemplateId);
          this.initNewSchedule();
          GlobalHelper.NotifySuccess('Schedule successfully created!');
        }
      } catch (error) {
        GlobalHelper.NotifyError('Schedule could not be created!');
      }
    }

    private rebuildCronExpression(source) {
      const regex = /[\d|\*]{1,2}(?=\/\d)/;

      return regex.test(source) ? source.replace(regex, this.monthlyStartingAt || '*') : source;
    }

    clearWarning() {
      this.warningMessage = '';
    }

    //TODO: I know this is a code smell. Looks like vue-cron-editor doesn't have any possibility to track the selected cron tab.
    isMonthlySelected() {
      const monthlyContainer = document.getElementById('schedule-cron-editor');

      if (!monthlyContainer) {
        this.resetMonthlyState();
        return;
      }

      const lastChild = monthlyContainer.querySelector('li:nth-child(3)');

      if (!lastChild) {
        this.resetMonthlyState();
        return;
      }

      this.isMonthlySelectedState =
        lastChild.classList.contains('is-active') &&
        lastChild.textContent &&
        lastChild.textContent.trim().toLowerCase() == this.cronTabs[this.cronTabs.length - 2];

      if (!this.isMonthlySelectedState) {
        this.resetMonthlyState();
      }
    }

    private resetMonthlyState() {
      this.isMonthlySelectedState = false;
      this.monthlyStartingAt = null;
    }
  }
</script>

<style scoped lang="scss">
  .schedules-list {
    max-height: 300px;
    overflow: hidden auto;
    margin-bottom: 16px;
  }
  div.action-bar {
    height: 30px;
    display: flex;
    flex-direction: row;
    align-items: center;
    button {
      flex: 0 0 auto;
    }
    .alert.alert-info {
      flex: 0 0 auto;
      display: inline-block;
      margin-left: 16px;
    }
  }
</style>

<style lang="scss">
  #schedule-cron-editor {
    div.weekly-tab {
      label.b-checkbox {
        margin-bottom: 0;
      }
    }
    div.monthly-tab {
      .field .centered-text {
        flex: 0 0 auto;
      }
    }
    .dropdown-item {
      .select {
        font-size: 14px;
        select {
          padding: 2px;
        }
      }
      .control.is-colon {
        font-size: 14px;
      }
    }
    .timepicker.control,
    .b-numberinput {
      .input {
        border-radius: 4px;
        margin: 2px;
        width: 100px;
      }
    }
  }
</style>
